import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_QAS, URL_QAS } from "@/constants/api_endpoints";
import {
	IAddPromptProps,
	IEditPromptProps,
	IQasPromptState,
	IResponseAddingPrompt,
	IResponsePrompt,
	IResponsePromptData,
	IResponsePromptList,
} from "@/types/qas/qasPromptTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: IQasPromptState = {
	promptList: {
		data: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	prompt: {
		data: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	addingPrompt: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		id: null,
	},
	editingPrompt: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	deletingPrompt: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// получение списка промптов
export const getPromptList = createAsyncThunk("qasPrompt/getPromptList", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponsePromptList | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.PROMPT_LIST}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// получение промпта
export const getPrompt = createAsyncThunk("qasPrompt/getPrompt", async (promptId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponsePrompt | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.PROMPT_GET}/${promptId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// добавление промпта
export const addPrompt = createAsyncThunk("qasPrompt/addPrompt", async ({ type, name, prompt, postprompt, params }: IAddPromptProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseAddingPrompt> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.PROMPT_ADD}`, {
			type,
			name,
			prompt,
			postprompt,
			params,
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// изменение промпта
export const editPrompt = createAsyncThunk("qasPrompt/editPrompt", async ({ promptId, data }: IEditPromptProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.PROMPT_EDIT}/${promptId}`, {
			data: JSON.stringify(data),
		});
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// удаление промпта
export const deletePrompt = createAsyncThunk("qasPrompt/deletePrompt", async (promptId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(`${URL_QAS}/${ENDPOINTS_QAS.PROMPT_DELETE}/${promptId}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const qasPromptSlice = createSlice({
	name: "qasPrompt",
	initialState,
	reducers: {
		// изменение данных промпта
		changePromptData: (state, action: PayloadAction<Pick<IResponsePromptData, "id" | "isDefault" | "name" | "prompt" | "postprompt" | "params">>) => {
			if (state.promptList.data) {
				const promptIndexFound = state.promptList.data.findIndex((promptItem) => promptItem.id === action.payload.id);
				if (promptIndexFound >= 0) {
					state.promptList.data[promptIndexFound] = {
						...state.promptList.data[promptIndexFound],
						name: action.payload.name,
						prompt: action.payload.prompt,
						postprompt: action.payload.postprompt,
						isDefault: action.payload.isDefault,
						params: action.payload.params,
					};
				}
			}
		},
		// очистка state
		clearState: (state) => {
			state.promptList = initialState.promptList;
			state.prompt = initialState.prompt;
			state.addingPrompt = initialState.addingPrompt;
			state.editingPrompt = initialState.editingPrompt;
			state.deletingPrompt = initialState.deletingPrompt;
		},
		// очистка списка промптов
		clearPromptList: (state) => {
			state.promptList = initialState.promptList;
		},
		// очистка промпта
		clearPrompt: (state) => {
			state.prompt = initialState.prompt;
		},
		// очистка статуса добавления промпта
		clearAddingPrompt: (state) => {
			state.addingPrompt = initialState.addingPrompt;
		},
		// очистка статуса изменения промпта
		clearEditingPrompt: (state) => {
			state.editingPrompt = initialState.editingPrompt;
		},
		// очистка статуса удаления промпта
		clearDeletingPrompt: (state) => {
			state.deletingPrompt = initialState.deletingPrompt;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getPromptList.pending, (state) => {
				state.promptList.status = RequestStatus.LOADING;
			})
			.addCase(getPromptList.fulfilled, (state, action) => {
				if (typeof action.payload === "object" && "prompts" in action.payload && Array.isArray(action.payload.prompts)) {
					state.promptList.status = RequestStatus.IDLE;
					state.promptList.data = action.payload.prompts.sort((a, b) => {
						if (a.name > b.name) return 1;
						else if (a.name < b.name) return -1;
						else return 0;
					});
				} else state.promptList.status = RequestStatus.FAILED;
			})
			.addCase(getPromptList.rejected, (state, action: PayloadAction<unknown>) => {
				state.promptList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.promptList.error = error;
				state.promptList.message = message;
			})
			.addCase(getPrompt.pending, (state) => {
				state.prompt.status = RequestStatus.LOADING;
			})
			.addCase(getPrompt.fulfilled, (state, action) => {
				if (typeof action.payload === "object" && "prompt" in action.payload) {
					state.prompt.status = RequestStatus.IDLE;
					state.prompt.data = action.payload.prompt;
				} else state.prompt.status = RequestStatus.FAILED;
			})
			.addCase(getPrompt.rejected, (state, action: PayloadAction<unknown>) => {
				state.prompt.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.prompt.error = error;
				state.prompt.message = message;
			})
			.addCase(addPrompt.pending, (state) => {
				state.addingPrompt.status = RequestStatus.LOADING;
			})
			.addCase(addPrompt.fulfilled, (state, action) => {
				if (action.payload) {
					state.addingPrompt.status = RequestStatus.IDLE;
					state.addingPrompt.error = action.payload.error;
					state.addingPrompt.message = action.payload.message;
					if (action.payload.id) state.addingPrompt.id = action.payload.id;
				} else state.addingPrompt.status = RequestStatus.FAILED;
			})
			.addCase(addPrompt.rejected, (state, action: PayloadAction<unknown>) => {
				state.addingPrompt.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.addingPrompt.error = error;
				state.addingPrompt.message = message;
			})
			.addCase(editPrompt.pending, (state) => {
				state.editingPrompt.status = RequestStatus.LOADING;
			})
			.addCase(editPrompt.fulfilled, (state, action) => {
				if (action.payload) {
					state.editingPrompt.status = RequestStatus.IDLE;
					state.editingPrompt.error = action.payload.error;
					state.editingPrompt.message = action.payload.message;
				} else state.editingPrompt.status = RequestStatus.FAILED;
			})
			.addCase(editPrompt.rejected, (state, action: PayloadAction<unknown>) => {
				state.editingPrompt.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.editingPrompt.error = error;
				state.editingPrompt.message = message;
			})
			.addCase(deletePrompt.pending, (state) => {
				state.deletingPrompt.status = RequestStatus.LOADING;
			})
			.addCase(deletePrompt.fulfilled, (state, action) => {
				if (action.payload) {
					state.deletingPrompt.status = RequestStatus.IDLE;
					state.deletingPrompt.error = action.payload.error;
					state.deletingPrompt.message = action.payload.message;
				} else state.deletingPrompt.status = RequestStatus.FAILED;
			})
			.addCase(deletePrompt.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingPrompt.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingPrompt.error = error;
				state.deletingPrompt.message = message;
			});
	},
});

export const { changePromptData, clearState, clearPromptList, clearPrompt, clearAddingPrompt, clearEditingPrompt, clearDeletingPrompt } = qasPromptSlice.actions;

export const selectPromptList = (state: RootState) => state.qasPrompt.promptList;
export const selectPrompt = (state: RootState) => state.qasPrompt.prompt;
export const selectAddingPrompt = (state: RootState) => state.qasPrompt.addingPrompt;
export const selectEditingPrompt = (state: RootState) => state.qasPrompt.editingPrompt;
export const selectDeletingPrompt = (state: RootState) => state.qasPrompt.deletingPrompt;

export default qasPromptSlice.reducer;
